// auditLogService.js
import { db, auth } from './firebase';
import { collection, addDoc, getDocs, query, where, orderBy, limit as fsLimit, Timestamp } from 'firebase/firestore';
import { fnUrl } from './functionsBase';
import { identityItem } from './identityCache';

const auditLogCollection = collection(db, 'auditLog');
const FN_LOG_FAILED_LOGIN = fnUrl('logFailedLogin');

const getCurrentUserName = () => {
  const storedName = identityItem('technicianName');
  if (storedName && storedName.length > 0) return storedName;
  if (auth.currentUser && auth.currentUser.email) return auth.currentUser.email.split('@')[0];
  return null;
};

// Scoped to "security + high-value admin actions" (login, user create/delete, role changes,
// invoice/estimate deletion) rather than every mutation across the app. `companyId` is the
// company the action actually happened in/against (the deleted invoice's own companyId, the
// affected user's companyIds[0], the company the caller just logged into) — every call site
// passes it explicitly rather than this function guessing at "the caller's current company",
// since a multi-company admin's current company isn't always well-defined. Entries with no
// companyId (there shouldn't be many going forward) are only ever visible to a platform
// super-admin — see firestore.rules' auditLog match block. Never throws — a logging failure
// should never block the real action it's describing.
export const logAuditEvent = async ({ action, category, severity = 'info', details = {}, companyId = null }) => {
  if (!auth.currentUser) return;
  try {
    await addDoc(auditLogCollection, {
      companyId,
      userId: auth.currentUser.uid,
      userEmail: auth.currentUser.email || null,
      userName: getCurrentUserName(),
      action,
      category,
      severity,
      details,
      createdAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error writing audit log entry:', error);
  }
};

// The one case with no authenticated user to write as (signInWithEmailAndPassword failed) —
// goes through functions/auditLog.js's logFailedLogin instead of a direct Firestore write.
// Fire-and-forget: a failed login should never be blocked or delayed by this.
export const logFailedLoginAttempt = (email) => {
  fetch(FN_LOG_FAILED_LOGIN, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email })
  }).catch((error) => console.error('Error logging failed login attempt:', error));
};

// A platform super-admin sees every company's entries (no where clause — provable under
// firestore.rules' isSuperAdmin() branch regardless of query shape); anyone else only ever sees
// their own company/companies' entries, which the client must filter for explicitly (a `list`
// query's rule can only reference resource.data.companyId when the query itself also filters on
// that exact field — see the big provability comment block at the top of firestore.rules).
// Impersonation is already audited (functions/masterAdmin.js's impersonateCompany writes a
// critical-severity 'impersonation_started' entry) but, before this, finding "who impersonated
// whom, when" meant manually filtering the same raw feed as every other admin action in
// AuditLogViewer.js. Super-admin only — enforced by the same firestore.rules isSuperAdmin()
// branch getAuditLog above relies on, since this has no companyId filter of its own either.
export const getImpersonationHistory = async (maxResults = 100) => {
  try {
    const q = query(auditLogCollection, where('action', '==', 'impersonation_started'), orderBy('createdAt', 'desc'), fsLimit(maxResults));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching impersonation history:', error);
    return [];
  }
};

// AUDIT ITEM 23. Same fix the ERROR log next door already got. A swallowed failure here told an
// admin there were no logins, no role changes and no impersonations — the most load-bearing "no
// records" message in the app, because it is the one somebody checks precisely when they suspect
// something happened. Opt-in, so no other caller changes.
export const getAuditLog = async (companyIds, isSuperAdmin, maxResults = 300, { throwOnError = false } = {}) => {
  try {
    const q = isSuperAdmin
      ? query(auditLogCollection, orderBy('createdAt', 'desc'), fsLimit(maxResults))
      : query(auditLogCollection, where('companyId', 'in', companyIds), orderBy('createdAt', 'desc'), fsLimit(maxResults));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error fetching audit log:', error);
    if (throwOnError) throw error;
    return [];
  }
};
